import { GeoJSONSource } from "mapbox-gl";
import MapboxViewer from "@/components/mapbox/MapboxViewer";
import { MapboxMeasureLengthObject } from "../Objects/MapboxMeasureLengthObject";
import { autobind } from "core-decorators";
import { MAPBOX_MIN_ZOOM } from "@/constant";

class MeasureLengthEntity {
  private _mapboxViewer: MapboxViewer;
  private _records: Map<string, MapboxMeasureLengthObject> = new Map();
  public sourceId = "measure-length-source";
  public labelSourceId = "measure-length-label-source";
  public source: GeoJSONSource | undefined = undefined;
  public labelSource: GeoJSONSource | undefined = undefined;
  public geojson: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: [],
  };
  public labelGeojson: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: [],
  };

  constructor(mapboxViewer: MapboxViewer) {
    this._mapboxViewer = mapboxViewer;
    this.init();
  }

  @autobind
  init() {
    // Create measure length source
    this.dispose();
    this._mapboxViewer._mapViewer.addSource(this.sourceId, {
      type: "geojson",
      data: this.geojson,
      minzoom: MAPBOX_MIN_ZOOM,
    });

    // Create label source
    this._mapboxViewer._mapViewer.addSource(this.labelSourceId, {
      type: "geojson",
      data: this.labelGeojson,
      minzoom: MAPBOX_MIN_ZOOM,
    });

    // Add length measurement layer
    this._mapboxViewer._mapViewer.addLayer({
      id: "measure-length-line",
      type: "line",
      source: this.sourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "category"], "measurement_length"],
        ["==", ["get", "visible"], true],
      ],
      paint: {
        "line-color": ["get", "color"],
        "line-width": 3,
        "line-dasharray": [2, 2],
      },
    });

    // Add label background layer
    this._mapboxViewer._mapViewer.addLayer({
      id: "measure-length-label-bg",
      type: "circle",
      source: this.labelSourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "category"], "measure_label"],
        [
          "in",
          ["get", "labelType"],
          ["literal", ["total_length", "segment_length"]],
        ],
        ["==", ["get", "visible"], true],
      ],
      paint: {
        "circle-radius": 8,
        "circle-color": "#ffffff",
        "circle-opacity": 0.9,
        "circle-stroke-color": "#cccccc",
        "circle-stroke-width": 1,
      },
    });

    // Add measurement labels layer
    this._mapboxViewer._mapViewer.addLayer({
      id: "measure-length-labels",
      type: "symbol",
      source: this.labelSourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "category"], "measure_label"],
        ["==", ["get", "visible"], true],
      ],
      layout: {
        "text-field": [
          "case",
          ["==", ["get", "labelType"], "total_length"],
          ["concat", "Total: ", ["get", "lengthFormatted"]],
          ["==", ["get", "labelType"], "segment_length"],
          ["get", "lengthFormatted"],
          "",
        ],
        "text-size": 12,
        "text-offset": [0, 0],
        "text-anchor": "center",
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": [
          "case",
          ["==", ["get", "labelType"], "total_length"],
          "#d32f2f",
          ["==", ["get", "labelType"], "segment_length"],
          "#f44336",
          "#000000",
        ],
        "text-halo-color": "#ffffff",
        "text-halo-width": 3,
        "text-halo-blur": 1,
      },
    });

    // Add vertex layers for editing
    this._mapboxViewer._mapViewer.addLayer({
      id: "measure-length-vertices",
      type: "circle",
      source: this.labelSourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "vertexType"], "corner"],
        ["==", ["get", "visible"], true],
      ],
      layout: {
        visibility: "none",
      },
      paint: {
        "circle-radius": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          8,
          ["boolean", ["feature-state", "hover"], false],
          7,
          6,
        ],
        "circle-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          "#ff4444",
          ["boolean", ["feature-state", "hover"], false],
          "#ff6666",
          "#ffffff",
        ],
        "circle-stroke-color": "#333333",
        "circle-stroke-width": 2,
      },
    });

    // Add midpoint vertices layer
    this._mapboxViewer._mapViewer.addLayer({
      id: "measure-length-midpoints",
      type: "circle",
      source: this.labelSourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "vertexType"], "midpoint"],
        ["==", ["get", "visible"], true],
      ],
      layout: {
        visibility: "none",
      },
      paint: {
        "circle-radius": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          6,
          4,
        ],
        "circle-color": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          "#00ff00",
          "#88ff88",
        ],
        "circle-stroke-color": "#333333",
        "circle-stroke-width": 1,
        "circle-opacity": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          1.0,
          0.7,
        ],
      },
    });

    this.source = this._mapboxViewer._mapViewer.getSource(
      this.sourceId
    ) as GeoJSONSource;
    this.labelSource = this._mapboxViewer._mapViewer.getSource(
      this.labelSourceId
    ) as GeoJSONSource;
  }

  @autobind
  add(measureObject: MapboxMeasureLengthObject) {
    this._records.set(measureObject.contentId, measureObject);
    this.updateSource();
  }

  @autobind
  remove(contentId: string) {
    this._records.delete(contentId);
    this.updateSource();
  }

  @autobind
  updateSource() {
    // Update measure features
    this.geojson.features = Array.from(this._records.values())
      .map((measureObject) => measureObject.getFeature())
      .filter((feature) => feature !== null) as GeoJSON.Feature[];

    // Update label features
    this.labelGeojson.features = Array.from(this._records.values())
      .flatMap((measureObject) => measureObject.getLabelFeatures())
      .filter((feature) => feature !== null) as GeoJSON.Feature[];

    this.source?.setData(this.geojson);
    this.labelSource?.setData(this.labelGeojson);
  }

  @autobind
  getById(contentId: string) {
    return this._records.get(contentId);
  }

  @autobind
  showVertices() {
    this._mapboxViewer._mapViewer.setLayoutProperty(
      "measure-length-vertices",
      "visibility",
      "visible"
    );
    this._mapboxViewer._mapViewer.setLayoutProperty(
      "measure-length-midpoints",
      "visibility",
      "visible"
    );
  }

  @autobind
  hideVertices() {
    this._mapboxViewer._mapViewer.setLayoutProperty(
      "measure-length-vertices",
      "visibility",
      "none"
    );
    this._mapboxViewer._mapViewer.setLayoutProperty(
      "measure-length-midpoints",
      "visibility",
      "none"
    );
  }

  @autobind
  dispose() {
    // Remove layers and sources
    const layers = [
      "measure-length-line",
      "measure-length-label-bg",
      "measure-length-labels",
      "measure-length-vertices",
      "measure-length-midpoints",
    ];
    layers.forEach((layerId) => {
      if (this._mapboxViewer._mapViewer.getLayer(layerId)) {
        this._mapboxViewer._mapViewer.removeLayer(layerId);
      }
    });

    const sources = [this.sourceId, this.labelSourceId];
    sources.forEach((sourceId) => {
      if (this._mapboxViewer._mapViewer.getSource(sourceId)) {
        this._mapboxViewer._mapViewer.removeSource(sourceId);
      }
    });

    this._records.clear();
  }
}

export default MeasureLengthEntity;
